import type { SettlementReceipt } from '@tira/protocol';
import type { JSX } from 'react';
import { Button } from '../components/Button.js';
import { useT, type T } from '../i18n/index.js';
import { socket } from '../net/socket.js';
import { useApp } from '../state/store.js';
import { chips, bps as bpsText, signedChips } from './lib/format.js';
import { Screen } from './parts/Screen.js';
import './Results.css';

/**
 * S12 — the settle. Every chip that moved is on one slip: the pot, the tong,
 * and each seat's signed delta. The tong is never summarised away (§10) — a
 * player who wants to check the house's cut can read it here without tapping.
 */
export function Results({ matchId }: { matchId: string }): JSX.Element {
  const receipt = useApp((s) => s.receipt);
  const me = useApp((s) => s.me);
  const rematch = useApp((s) => s.rematch);
  const tableId = useApp((s) => s.table?.tableId ?? s.match?.tableId ?? null);
  const go = useApp((s) => s.go);
  const t = useT();
  /**
   * Bots are always labeled — PRD §8.1 — and a receipt line carries a handle
   * but no `isBot`, so the label comes off the table snapshot by seat index.
   * Only trusted when the snapshot is this match's table; an unlabeled seat is
   * better than a wrongly labeled one. Selected as the raw array so the
   * selector keeps returning the same reference.
   */
  const tableSeats = useApp((s) =>
    s.match !== null && s.table?.tableId === s.match.tableId ? s.table.seats : null,
  );
  const isBot = (seat: number): boolean =>
    tableSeats?.some((x) => x !== null && x.seat === seat && x.isBot) === true;

  if (!receipt) {
    return (
      <Screen title={t('results.title')}>
        <div className="s-empty">
          <b>{t('results.empty.title')}</b>
          <span>{t('results.empty.body')}</span>
          <Button variant="secondary" onClick={() => go({ s: 'compound' })}>
            {t('results.back')}
          </Button>
        </div>
      </Screen>
    );
  }

  const lines = [...receipt.lines].sort((a, b) => a.placement - b.placement);
  const mine = lines.find((l) => l.userId === me?.userId) ?? null;
  /**
   * Todas is *sweeping the table*, not merely being the only one paid. In a
   * two-seat winner-take-all venue those are arithmetically identical, so the
   * stamp landed on every dama win — including one where the opponent simply
   * quit, printing "Nilinis mo ang mesa" directly under "May sumuko". A hand
   * somebody walked out of was never a clean sweep, whatever the ledger says.
   */
  const sole = receipt.reason === 'win' ? soleWinner(receipt) : null;
  const todas = sole !== null;
  /** A miron has no line on this receipt, and no seat to bring to a rematch. */
  const watching = mine === null;

  return (
    <Screen title={t('results.title')} sub={reasonText(t, receipt.reason)}>
      <div className="results">
        <div className="results__verdict">
          <h1>{verdictText(t, mine?.delta ?? null, todas && sole?.userId === me?.userId)}</h1>
          {mine ? (
            <span
              className="results__delta"
              data-sign={mine.delta > 0 ? 'up' : mine.delta < 0 ? 'down' : 'flat'}
            >
              {signedChips(mine.delta)}
            </span>
          ) : null}
        </div>

        {todas && sole ? (
          <div className="s-panel todas">
            <p className="todas__word">TODAS</p>
            <p className="todas__who">
              {sole.userId === me?.userId
                ? t('results.todas.you')
                : t('results.todas.them', {
                    who: `${sole.handle}${isBot(sole.seat) ? ` (${t('results.ai')})` : ''}`,
                  })}
            </p>
          </div>
        ) : null}

        <Slip receipt={receipt} todas={todas} isBot={isBot} />

        <div className="s-panel s-panel--quiet">
          <div className="results__rematch">
            {/*
              A miron gets the receipt too — they were watching the hand — but
              "Bawi ako" is a chair they never held. The server no-ops it, so
              the button used to sit there enabled and do nothing.
            */}
            <Button
              variant="primary"
              disabled={!tableId || watching}
              onClick={() => {
                if (tableId) socket.send({ t: 'rematch', tableId, want: true });
              }}
            >
              {watching ? t('results.rematch.watching') : t('results.rematch.go')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (tableId) socket.send({ t: watching ? 'spectate.leave' : 'table.leave', tableId });
                go({ s: 'compound' });
              }}
            >
              {watching ? t('results.back') : t('results.leave.table')}
            </Button>
            <button className="s-linkbtn" onClick={() => go({ s: 'fairplay', matchId })}>
              {t('results.fairlink')}
            </button>
          </div>
          <p className="s-note">
            {rematch
              ? t('results.note.ready', { ready: rematch.ready.length, needed: rematch.needed })
              : tableId
                ? t('results.note.agree')
                : t('results.note.closed')}
          </p>
        </div>
      </div>
    </Screen>
  );
}

/**
 * Todas: one seat walked off with the entire post-tong pot and every other seat
 * lost its whole stake.
 *
 * The arithmetic matters, because a receipt line's `delta` is *net of the
 * buy-in* — the server sums the seat's ledger entries for the match, which
 * includes the escrowed stake. So the winner of a 1,000-chip two-seat hand
 * shows +900 against a `net` of 1,900, not +1,900. Every seat at a table stakes
 * the same buy-in (the table carries one), so the stake is `pot / seats` and
 * the test is `delta + stake === net`.
 */
export function soleWinner(receipt: SettlementReceipt): SettlementReceipt['lines'][number] | null {
  const lines = receipt.lines;
  if (lines.length < 2) return null;
  const winners = lines.filter((l) => l.delta > 0);
  const sole = winners[0];
  if (winners.length !== 1 || sole === undefined) return null;
  if (!lines.every((l) => l === sole || l.delta < 0)) return null;
  const stake = receipt.pot / lines.length;
  // Tolerance of one chip: the pot is integral and the stake uniform, so this
  // only absorbs a table that somehow settled with mismatched buy-ins.
  return Math.abs(sole.delta + stake - receipt.net) < 1 ? sole : null;
}

function Slip({
  receipt,
  todas,
  isBot,
}: {
  receipt: SettlementReceipt;
  todas: boolean;
  isBot: (seat: number) => boolean;
}): JSX.Element {
  const t = useT();
  const lines = [...receipt.lines].sort((a, b) => a.placement - b.placement);
  return (
    <article className="receipt">
      {todas ? <span className="receipt__stamp">TODAS</span> : null}
      <div className="receipt__head">
        <b>{t('results.slip.head')}</b>
        <span>{receipt.matchId.slice(0, 8)}</span>
      </div>

      {lines.map((l) => (
        <p className="receipt__line" key={`${l.seat}-${l.userId}`}>
          <span className="who">
            <span className="rank">{l.placement}</span>
            {l.handle}
            {/* Bots are always labeled — PRD §8.1. The slip is no exception. */}
            {isBot(l.seat) ? <span className="receipt__ai">{t('results.ai')}</span> : null}
          </span>
          <span className="lead" />
          <span>{signedChips(l.delta)}</span>
        </p>
      ))}

      <div className="receipt__rule" />

      <p className="receipt__line">
        <span className="who">{t('results.slip.pot')}</span>
        <span className="lead" />
        <span>{chips(receipt.pot)}</span>
      </p>

      {/* The house cut, spelled out. This line is the point of the screen. */}
      <p className="receipt__tong">
        <span className="who">
          {t('results.slip.tong')}{' '}
          <span className="why">
            {t('results.slip.tong.why', { pct: bpsText(receipt.tongBps) })}
          </span>
        </span>
        <span className="lead" />
        <span>{signedChips(-receipt.tong)}</span>
      </p>

      <div className="receipt__total">
        <span>{t('results.slip.net')}</span>
        <span>{chips(receipt.net)}</span>
      </div>

      <p className="receipt__foot">
        {t('results.slip.commit', {
          algo: receipt.fairness.algorithm,
          commit: receipt.fairness.commit.slice(0, 24),
        })}
        <br />
        {t('results.disclosure')}
      </p>
    </article>
  );
}

function verdictText(t: T, delta: number | null, todasMine: boolean): string {
  if (todasMine) return t('results.verdict.todas');
  if (delta === null) return t('results.verdict.over');
  if (delta > 0) return t('results.verdict.win');
  if (delta < 0) return t('results.verdict.loss');
  return t('results.verdict.draw');
}

function reasonText(t: T, reason: SettlementReceipt['reason']): string {
  return t(`results.reason.${reason}`);
}
